import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getMyInvitations, acceptInvitationById, rejectInvitationById } from '../../services/api';
import { useToast } from '../../hooks/useToast';

const ROLE_COLORS = { editor: '#10b981', viewer: '#6366f1', owner: '#f59e0b' };
const POLL_INTERVAL = 30_000;

export default function InvitesBell({ onAccepted }) {
  const [open, setOpen] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null); // invitation id being acted on
  const panelRef = useRef(null);
  const toast = useToast();

  const fetchInvites = useCallback(async () => {
    try {
      const data = await getMyInvitations();
      setInvites(data);
    } catch {}
  }, []);

  // Poll every 30s + fetch on open
  useEffect(() => {
    fetchInvites();
    const t = setInterval(fetchInvites, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [fetchInvites]);

  useEffect(() => {
    if (open) fetchInvites();
  }, [open, fetchInvites]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleAccept = async (inv) => {
    setActing(inv.id);
    try {
      await acceptInvitationById(inv.id);
      setInvites(prev => prev.filter(i => i.id !== inv.id));
      toast.success(`Joined "${inv.project_name}" as ${inv.role}`);
      onAccepted?.(inv.project_id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActing(null);
    }
  };

  const handleReject = async (inv) => {
    setActing(inv.id);
    try {
      await rejectInvitationById(inv.id);
      setInvites(prev => prev.filter(i => i.id !== inv.id));
      toast.success('Invitation declined');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActing(null);
    }
  };

  const count = invites.length;

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative', width: 36, height: 36, borderRadius: '50%',
          background: open ? 'rgba(99,102,241,0.15)' : 'transparent',
          border: '1px solid ' + (open ? 'rgba(99,102,241,0.4)' : 'transparent'),
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, transition: 'all 0.2s',
        }}
        title="Collaboration Invites"
      >
        🔔
        {count > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            minWidth: 16, height: 16, borderRadius: 8,
            background: '#ef4444', color: '#fff',
            fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', lineHeight: 1,
            boxShadow: '0 0 0 2px var(--bg-secondary)',
          }}>
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 44, right: 0, zIndex: 10000,
          width: 340, background: 'var(--bg-card)',
          border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px 10px', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
              🤝 Collaboration Invites
            </span>
            {count > 0 && (
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#ef4444',
                background: 'rgba(239,68,68,0.1)', padding: '2px 8px', borderRadius: 10,
              }}>
                {count} pending
              </span>
            )}
          </div>

          {/* Invite list */}
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {invites.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>No pending invites</p>
              </div>
            ) : (
              invites.map(inv => (
                <div key={inv.id} style={{
                  padding: '14px 16px', borderBottom: '1px solid var(--border)',
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  {/* Project info */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                      background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16,
                    }}>
                      📁
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inv.project_name}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
                        Invited by <strong style={{ color: 'var(--text-secondary)' }}>{inv.invited_by_email}</strong>
                      </p>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 10, flexShrink: 0,
                      color: ROLE_COLORS[inv.role] || '#6366f1',
                      background: `${ROLE_COLORS[inv.role] || '#6366f1'}20`,
                    }}>
                      {inv.role === 'editor' ? '✏️ Editor' : '👁 Viewer'}
                    </span>
                  </div>

                  {/* Role description */}
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    {inv.role === 'editor'
                      ? 'You can view and edit files in this project.'
                      : 'You can view files in this project (read-only).'}
                  </p>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleAccept(inv)}
                      disabled={acting === inv.id}
                      style={{
                        flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 600,
                        background: 'linear-gradient(135deg,#10b981,#059669)',
                        color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer',
                        opacity: acting === inv.id ? 0.6 : 1,
                      }}
                    >
                      {acting === inv.id ? '...' : '✓ Accept'}
                    </button>
                    <button
                      onClick={() => handleReject(inv)}
                      disabled={acting === inv.id}
                      style={{
                        flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 600,
                        background: 'transparent', color: 'var(--text-muted)',
                        border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer',
                        opacity: acting === inv.id ? 0.6 : 1,
                      }}
                    >
                      ✕ Decline
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
